import ActionButton from "@/components/buttons/ActionButton";
import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { sanitize } from "@/utils/formatUtils";
import { cssVariables } from "@/utils/utils";
import { Card, Input, Space, Tree, type TreeDataNode as DataTreeNode } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetAgentTreeNode } from "../../../../common/api/queries";
import { Permission } from "../../../../common/security/enums";
import { useHasPermissions } from "../../../auth/hooks";
import { AgentTree, AgentTreeFilterRequest, AgentTreeNode } from "../../types";
import { AgentTreeColumnsWidth } from "../../utils";
import { AgentTreeNodeTitle } from "./AgentTreeNodeTitle";

const PADDING = 36;

type DataNode = DataTreeNode & { agent: AgentTreeNode; children?: DataNode[] };

const updateTreeData = (list: DataNode[], key: React.Key, children: DataNode[]): DataNode[] => {
  const newList: DataNode[] = [...list];
  const stack: DataNode[][] = [newList];

  while (stack.length > 0) {
    const nodes = stack.pop();
    if (!nodes) {
      continue;
    }

    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];

      if (!node) {
        continue;
      }

      if (node.key === key) {
        nodes[i] = { ...node, children };
        return newList;
      }

      if (node.children && node.children.length) {
        const copiedChildren: DataNode[] = [...node.children];
        nodes[i] = { ...node, children: copiedChildren };
        stack.push(copiedChildren);
      }
    }
  }

  return newList;
};

const createRootNode = (agentTree: AgentTree, sanitizedSearchKeyword?: string) => ({
  title: (
    <AgentTreeNodeTitle
      agent={{ ...agentTree.agent, hasSubordinates: !!agentTree.agent.subordinates.length }}
      rootAgentAccessTreePath={agentTree.agent.accessTreePath}
      sanitizedSearchKeyword={sanitizedSearchKeyword}
    />
  ),
  agent: { ...agentTree.agent, hasSubordinates: !!agentTree.agent.subordinates.length },
  key: agentTree.agent.id,
  children: buildTreeNodes(agentTree.agent.subordinates, agentTree.agent.accessTreePath, sanitizedSearchKeyword),
});

const buildTreeNodes = (
  agents: AgentTreeNode[],
  rootAgentAccessTreePath?: string,
  sanitizedSearchKeyword?: string,
): DataNode[] => {
  return agents.map<DataNode>((agent) => ({
    title: (
      <AgentTreeNodeTitle
        agent={agent}
        sanitizedSearchKeyword={sanitizedSearchKeyword}
        rootAgentAccessTreePath={rootAgentAccessTreePath}
      />
    ),
    key: agent.id,
    isLeaf: !agent.hasSubordinates,
    selectable: false,
    agent,
  }));
};

const updateTreeWithKeyword = (nodes: DataNode[], rootAgentAccessTreePath?: string, keyword?: string): DataNode[] =>
  nodes.map((node) => ({
    ...node,
    title: (
      <AgentTreeNodeTitle
        agent={{ ...node.agent, hasSubordinates: !!node.children?.length }}
        sanitizedSearchKeyword={keyword}
        rootAgentAccessTreePath={rootAgentAccessTreePath}
      />
    ),
    children: node.children ? updateTreeWithKeyword(node.children, rootAgentAccessTreePath, keyword) : undefined,
  }));

interface Props {
  agentTree: AgentTree;
  searchParams: AgentTreeFilterRequest;
}

export const AgentTreeView = ({ agentTree, searchParams: { rootAgentId, ...searchParams } }: Props) => {
  const { t } = useTranslation();

  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([agentTree.agent.id]);
  const [sanitizedSearchKeyword, setSanitizedSearchKeyword] = useState<string>();
  const [treeData, setTreeData] = useState<DataNode[]>(() => [createRootNode(agentTree, sanitizedSearchKeyword)]);

  const hasAgentCommissionsPermission = useHasPermissions(Permission.AGENT_COMMISSIONS);

  useEffect(() => {
    setTreeData([createRootNode(agentTree, sanitizedSearchKeyword)]);

    setExpandedKeys([agentTree.agent.id]);
  }, [agentTree]);

  const getAgentTreeNode = useGetAgentTreeNode();

  const handleSearchSubmit = (keyword: string) => {
    const newSanitizedSearchKeyword = keyword.trim() ? sanitize(keyword) : undefined;

    setSanitizedSearchKeyword(newSanitizedSearchKeyword);
    setTreeData((prevTreeData) =>
      updateTreeWithKeyword(prevTreeData, agentTree.agent.accessTreePath, newSanitizedSearchKeyword),
    );
  };

  const pointsWidth = hasAgentCommissionsPermission ? 0 : AgentTreeColumnsWidth.POINTS;

  return (
    <Card
      className="card-box"
      title={
        <Space size="small">
          <ActionButton icon="arrows" onClick={() => setExpandedKeys([agentTree.agent.id])}>
            {t("agent.actions.expandTree")}
          </ActionButton>
          <ActionButton icon="shrink" onClick={() => setExpandedKeys([])}>
            {t("agent.actions.collapseTree")}
          </ActionButton>
        </Space>
      }
      extra={
        <Input.Search
          style={{ width: 340, margin: "10px 0" }}
          enterButton
          allowClear
          placeholder={t("agent.helpers.treeSearchHint")}
          onSearch={(keyword) => handleSearchSubmit(keyword)}
        />
      }
    >
      <div style={{ maxWidth: "100%", overflowX: "auto", overflowY: "hidden", paddingTop: 10 }}>
        <div
          style={{
            paddingLeft: PADDING,
            paddingBottom: 4,
            marginBottom: 12,
            borderBottom: `1px solid ${cssVariables.colorShadow}`,
            display: "inline-flex",
          }}
        >
          <div style={{ width: AgentTreeColumnsWidth.AGENT - PADDING - 8 + pointsWidth }}>{t("common.agent")}</div>
          <div style={{ width: AgentTreeColumnsWidth.ID_NUMBER, padding: "0 8px" }}>{t("agent.attrs.idNumber")}</div>
          <div style={{ width: AgentTreeColumnsWidth.IDENTIFIER, padding: "0 8px" }}>{t("common.identifier")}</div>

          <div style={{ width: AgentTreeColumnsWidth.LEVEL, padding: "0 8px" }}>
            {t("agent.helpers.commissionsSettingsLevel")}
          </div>

          <div style={{ width: AgentTreeColumnsWidth.RANK, padding: "0 8px" }}>
            {t("agent.helpers.commissionsSettingsRank")}
          </div>

          <div
            style={{
              width: AgentTreeColumnsWidth.POINTS,
              textAlign: "right",
              padding: "0 8px",
              display: pointsWidth > 0 ? "none" : "block",
            }}
          >
            <LabelWithTooltip tooltip={t("agent.helpers.pointsHint")} label={t("agent.helpers.points")} />
          </div>

          <div style={{ width: AgentTreeColumnsWidth.TIPPER + AgentTreeColumnsWidth.ACTIONS }} />
        </div>

        <Tree
          styles={{ root: { width: Object.values(AgentTreeColumnsWidth).reduce((a, b) => a + b, 0) } }}
          loadedKeys={expandedKeys}
          loadData={async ({ key }) => {
            const agentTreeNode = await getAgentTreeNode.mutateAsync({ rootAgentId: key.toString(), ...searchParams });

            return setTreeData((rootTreeData) =>
              updateTreeData(
                rootTreeData,
                key,
                buildTreeNodes(
                  agentTreeNode.agent.subordinates,
                  agentTree.agent.accessTreePath,
                  sanitizedSearchKeyword,
                ),
              ),
            );
          }}
          treeData={treeData}
          expandedKeys={expandedKeys}
          onExpand={setExpandedKeys}
          showLine
        />
      </div>
    </Card>
  );
};
